import { hasOwnProperty } from "../../infra3d";
import { FeatureStyle } from "../featuretree/internal";
import { Infra3DGeoserver } from "./api/internal";
import { Layer, LAYERTYPE } from "./internal";
/**
 * Extension of the Layer-Class for Layers using the Infra3DGeoserver REST-API.
 * @class Infra3DLayer
 */
export class Infra3DLayer extends Layer {
    constructor(scene, opt_options) {
        super(scene, opt_options);
        this.type = LAYERTYPE.INFRA3DLAYER;
        const options = opt_options || {};
        // Initialization of the api. There are 2 possibilities:
        // 1. The api-object is passed in the option "api". (usually done, if the api has already been used bofore
        // Layer-Initialization, e.g. to load the layerdefinition).
        // 2. The baseurl, project and the apioptions are passed in the options.
        this.api =
            options.api || new Infra3DGeoserver(options.baseurl, options.project, scene.viewer, options.apioptions);
        this._featureclass = options.layername || "";
        this.layername = this.api.project + "." + this.layername;
    }
    /**
     * Infra3DLayer-specific implementation of the feature-load-function. This function can still be overwritten
     * by a given function 'loadFeatures' in the options.
     * @param extent
     * @param srid
     */
    _loadFeatures_internal(extent, srid) {
        const filters = this._applyFilter();
        const defaultVars = {
            bbox: extent.minx + "," + extent.miny + "," + extent.maxx + "," + extent.maxy,
            requestsrid: srid,
            requestoutputsrid: srid,
        };
        const urlVars = { ...defaultVars, ...filters.queryable, ...filters.fieldquery };
        this.api.readfeatures(this._featureclass, (geojson) => {
            this.addFeatures(geojson);
        }, (message) => {
            console.log(message);
        }, urlVars);
    }
    /**
     *  Saves all the changes done by the LayerEditor with the api. Resets the LayerEditor if the posts are
     *  successful.
     */
    _saveFeatures_internal(inserts, updates, deletions, cbs, cbf) {
        //Insert / Update
        const geojson_addupdate = JSON.parse(JSON.stringify(inserts));
        let featuresupdate = updates.features;
        for (const key in featuresupdate) {
            if (hasOwnProperty(featuresupdate, key)) {
                geojson_addupdate.features.push(featuresupdate[key]);
            }
        }
        this.api.addupdatefeatures(this._featureclass, geojson_addupdate, (resp_geojson) => {
            featuresupdate = {};
            const res_inserts = JSON.parse(JSON.stringify(resp_geojson));
            res_inserts.features = resp_geojson.features.slice(0, inserts.features.length);
            const res_updates = JSON.parse(JSON.stringify(resp_geojson));
            res_updates.features = resp_geojson.features.slice(inserts.features.length, inserts.features.length + updates.features.length);
            cbs(res_inserts, res_updates, null);
        }, cbf);
        //Delete
        if (deletions.features.length > 0) {
            this.api.deletefeatures(this._featureclass, deletions, (response_ids) => {
                cbs(null, null, deletions);
            }, cbf);
        }
    }
    /**
     * @param cbs
     * @param cbf
     * @param options
     */
    _exportFeatures_internal(cbs, cbf, options) {
        const filters = this._applyFilter();
        const sorters = options.sorters || [];
        let order_by = "";
        let dir = "";
        if (sorters.length > 0) {
            order_by = sorters[0].property;
            dir = sorters[0].direction;
        }
        const defaultVars = {
            count: typeof options.countonly === "undefined" ? false : options.countonly,
            offset: options.start || 0,
            limit: options.limit || 10000,
            no_geom: typeof options.nogeom === "undefined" ? false : options.nogeom,
            requestoutputsrid: options.srid || "",
            order_by: order_by,
            dir: dir,
        };
        const urlVars = { ...defaultVars, ...filters.queryable, ...filters.fieldquery };
        this.api.readfeatures(this._featureclass, cbs, cbf, urlVars);
    }
    _applyFilter() {
        //apply the filter
        const queryable = [];
        const fieldquery = {};
        for (const key in this.filters) {
            if (hasOwnProperty(this.filters, key)) {
                const filter = this.filters[key];
                if (queryable.indexOf(filter.property) == -1)
                    queryable.push(filter.property);
                fieldquery[filter.property + "_" + filter.operator] = filter.value;
            }
        }
        return { queryable: { queryable: queryable.join() }, fieldquery: fieldquery };
    }
    /**
     * Parses the layerdefinition (used in infra3dgeoserver) to the specified layer-options.
     * @param layerdefinition
     * @param opt_options
     * @returns {any|{}}
     */
    static optionsFromLayerdefinition(layerdefinition, opt_options) {
        if (hasOwnProperty(layerdefinition, "version")) {
            if (layerdefinition["version"] == "2.0") {
                return Infra3DLayer.optionsFromLayerdefinitionV2(layerdefinition, opt_options);
            }
        }
        const options = opt_options || {};
        const parsed_options = {};
        if (hasOwnProperty(layerdefinition, "styles")) {
            for (let i = 0; i < layerdefinition.styles.length; i++) {
                const mystyle = new FeatureStyle(layerdefinition.styles[i]);
                if (layerdefinition.styles[i]["stylename"] == "layerstyle") {
                    parsed_options["style"] = JSON.parse(mystyle.toJSON());
                }
                else if (layerdefinition.styles[i]["stylename"] == "highlightstyle") {
                    parsed_options["highlightstyle"] = JSON.parse(mystyle.toJSON());
                }
            }
        }
        if (hasOwnProperty(layerdefinition, "geometry")) {
            parsed_options["geometrytype"] = layerdefinition["geometry"];
        }
        if (hasOwnProperty(layerdefinition, "properties")) {
            parsed_options["propertydef"] = [];
            for (let i = 0; i < layerdefinition.properties.length; i++) {
                parsed_options["propertydef"].push(layerdefinition.properties[i]);
            }
        }
        layerdefinition = { ...layerdefinition, ...parsed_options };
        layerdefinition = { ...layerdefinition, ...options };
        return layerdefinition;
    }
    /**
     * @method optionsFromLayerdefinitionV2
     * @param layerdefinition
     * @param opt_options
     * @returns {any}
     */
    static optionsFromLayerdefinitionV2(layerdefinition, opt_options) {
        const options = opt_options || {};
        layerdefinition = { ...layerdefinition, ...options };
        return layerdefinition;
    }
}
//# sourceMappingURL=infra3dlayer.js.map